import React, { useMemo } from 'react';
import { formatCalls, formatShare, SEMANTIC_COLORS } from './nodeChartTheme';

/**
 * Воронка по этапам.
 *
 * Ключевой момент — пересчёт в накопительные значения. Выход `funnel_stage` объявлен как
 * «самый поздний достигнутый этап», то есть звонок попадает ровно в одну категорию: тот
 * этап, на котором он остановился. Нарисовать эти числа напрямую (что и делал общий
 * столбчатый график) — значит показать распределение точек остановки, а не воронку:
 * последний этап оказывался маленьким, хотя дошедших до него столько же, сколько в нём
 * и застряло, а первый — маленьким вместо «все звонки».
 *
 * Поэтому «дошли до этапа i» = сумма застрявших на i и на всех последующих этапах.
 * Порядок этапов берётся объявленный (`sort: declared`), а не по убыванию значений —
 * иначе пошаговая конверсия считалась бы между случайными парами.
 *
 * На карточке видно оба числа: накопительное «дошли» и «остановились здесь». Клик ведёт
 * в журнал по второму — только его и умеет выразить DSL фильтра (сравнение с одним кодом),
 * и пользователь кликает по числу, которое перед ним.
 */
const NodeFunnelChart = ({ rows, onSelect }) => {
  const stages = useMemo(() => {
    let reached = 0;

    // Идём с конца: на последнем этапе «дошли» совпадает со «остановились здесь».
    const cumulative = [...rows].reverse().map((row) => {
      reached += row.calls;
      return { ...row, reached };
    });

    cumulative.reverse();

    const first = cumulative[0]?.reached ?? 0;

    return cumulative.map((stage, index) => {
      const previous = index > 0 ? cumulative[index - 1].reached : null;

      return {
        ...stage,
        shareOfFirst: first > 0 ? (stage.reached * 100) / first : 0,
        stepConversion: previous ? (stage.reached * 100) / previous : null
      };
    });
  }, [rows]);

  if (stages.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2">
      {stages.map((stage, index) => (
        <React.Fragment key={stage.code}>
          {stage.stepConversion !== null && (
            <div className="pl-3 text-xs text-muted-foreground">
              ↓ конверсия шага: {formatShare(stage.stepConversion)}
            </div>
          )}

          <button
            type="button"
            data-ev="nodes.funnel.select"
            onClick={() => onSelect?.(stage)}
            disabled={!onSelect || stage.calls === 0}
            title={
              stage.calls > 0
                ? `Открыть звонки, остановившиеся на этапе «${stage.label}»`
                : 'На этом этапе не остановился ни один звонок'
            }
            className="w-full rounded-md border bg-muted/20 p-3 text-left transition-colors hover:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-ring disabled:cursor-default disabled:hover:bg-muted/20"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-medium">{stage.label}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  остановились здесь: {formatCalls(stage.calls)}
                </div>
              </div>
              <div className="shrink-0 text-right">
                <div className="text-sm font-semibold">{formatCalls(stage.reached)}</div>
                <div className="text-xs text-muted-foreground">{formatShare(stage.shareOfFirst)}</div>
              </div>
            </div>

            <div className="mt-2 h-2 rounded-full bg-gray-200 dark:bg-gray-800">
              <div
                className="h-2 rounded-full"
                style={{
                  // Минимум 2%, иначе нулевой этап выглядит как отсутствующая строка.
                  width: `${Math.max(2, Math.min(100, stage.shareOfFirst))}%`,
                  backgroundColor: SEMANTIC_COLORS.progress,
                  opacity: index === 0 ? 1 : Math.max(0.55, 1 - index * 0.1)
                }}
              />
            </div>
          </button>
        </React.Fragment>
      ))}
    </div>
  );
};

export default NodeFunnelChart;
